// EXTERNAL DEPENDENCIES
import React from 'react'
import { useDispatch } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'

// HELPERS
import {
  useBusinessProfilePageContent,
  useBusinessProfileReduxState,
} from '@/screens/business-profile-page/hooks'

// COMPONENTS
import ProfileCard from '@/common/components/ui/profile/profile-card'
import { NavigationButtons } from '../navigation-buttons'

// API
import { useEditProfile } from '@/api/profile'
import { formatProfileRequestBody } from '@/api/formatters'

// REDUX
import {
  updateIsEditing,
  updateEditPageIsLoading,
  updateEditPageErrorMessage,
  updateAreValuesEdited,
} from '@/redux/features'
import { updateGlobalToast } from '@/redux/features/global-slice'

// FPTI
import { scrollToTop, trackImpression } from '@/utils'
import {
  EDIT_PROFILE_AGREE_AND_PUBLISH_CLICK_FPTI,
  EDIT_PROFILE_PUBLISH_ERROR_FPTI,
  EDIT_PROFILE_PUBLISH_SUCCESS_FPTI,
  EDIT_PROFILE_TERMS_LINK_CLICK_FPTI,
} from './fpti'

// CONSTANTS
import { routes } from '@/api/constants'

// TYPES
import {
  ContactInfoResponse,
  LocationDisplayPreference,
  PortableAddress,
  ProfileData,
} from '@/types'

/**
 * Displays the Profile Card in reviewable state for the user to
 * view prior to saving their changes and publishing
 */
export function ReviewProfilePanel() {
  // INITIALIZATIONS
  const dispatch = useDispatch()

  // NETWORK REQUESTS
  const queryClient = useQueryClient()
  const { mutateAsync: editProfile } = useEditProfile()

  // STATE
  const {
    profileData = {},
    editedProfileData: { values },
  } = useBusinessProfileReduxState()

  // INITIALIZE CONTENT
  const getContent = useBusinessProfilePageContent()
  const publishErrorToastMessage = getContent('publishErrorToast')
  const publishSuccessToastMessage = getContent('publishSuccessToast')

  const {
    businessHandle,
    coverPhoto,
    businessName,
    profilePhoto,
    businessDescription,
    contactInfo = {},
    businessAddress,
    profilePreferences,
  } = values

  const { businessWebsiteUrl, businessEmail, businessPhoneNumber } =
    contactInfo as ContactInfoResponse
  const { businessJoinDate, customerCount } = profileData || {}

  async function handleSubmit() {
    const handleError = () => {
      dispatch(updateEditPageErrorMessage(publishErrorToastMessage))
      trackImpression(EDIT_PROFILE_PUBLISH_ERROR_FPTI)
    }

    try {
      scrollToTop()

      const request = formatProfileRequestBody(
        {
          businessDescription,
          businessAddress: businessAddress as PortableAddress,
          profilePhoto,
          coverPhoto,
          selectedContactInfo: contactInfo,
          selectedLocationDisplayPreference:
            profilePreferences.locationDisplayPreference,
        } as ProfileData,
        true,
      )

      dispatch(updateEditPageIsLoading(true))

      await editProfile({ request })

      dispatch(updateEditPageIsLoading(false))

      queryClient.refetchQueries({
        queryKey: [`${routes.PROFILE_DATA}/${businessHandle}`],
      })

      trackImpression(EDIT_PROFILE_PUBLISH_SUCCESS_FPTI)

      dispatch(updateGlobalToast({ message: publishSuccessToastMessage }))
      dispatch(updateIsEditing(false))
      dispatch(updateAreValuesEdited(false))
    } catch (_e) {
      dispatch(updateEditPageIsLoading(false))

      handleError()
    }
  }

  return (
    <div>
      <ProfileCard
        isReviewing
        userOwnsProfile
        data={{
          businessHandle,
          businessName,
          businessDescription,
          businessAddress: businessAddress as PortableAddress,
          accountCreateDate: businessJoinDate,
          pastYearCustomerCount: customerCount,
          contactInfo: {
            businessEmail,
            businessPhoneNumber,
            businessWebsiteUrl,
          },
          coverPhotoSrc: coverPhoto,
          profilePhotoSrc: profilePhoto,
          reviewHeaderText: getContent('reviewHeader'),
          reviewSubheaderText: getContent('reviewSubheader'),
          selectedLocationDisplayPreference:
            profilePreferences?.locationDisplayPreference as LocationDisplayPreference,
        }}
        isLoading={!businessName}
        fpti={{ disclaimerLinkFpti: EDIT_PROFILE_TERMS_LINK_CLICK_FPTI }}
      />
      <NavigationButtons
        isFormValid
        handleSubmit={handleSubmit}
        nextButtonProps={{
          text: getContent('agreeAndPublish'),
          fpti: EDIT_PROFILE_AGREE_AND_PUBLISH_CLICK_FPTI,
        }}
        backButtonProps={{
          handleClick: () => dispatch(updateIsEditing(false)),
        }}
      />
    </div>
  )
}
